import { useEffect, useRef, type CSSProperties } from "react";
import { fmt, fromRaw } from "../lib/num";
import { resultOf, type Play } from "../state/record";

/** `▲ UP 100×` or `▼ DOWN 100×`. */
export function Call({ isLong, leverage, compact = false }: { isLong: boolean; leverage?: number; compact?: boolean }) {
  return (
    <span className={`side-tag ${isLong ? "up" : "down"}`}>
      <span aria-hidden>{isLong ? "▲" : "▼"}</span>
      {compact ? null : isLong ? "Up" : "Down"}
      {leverage !== undefined && <span className="num">{leverage}×</span>}
    </span>
  );
}

/** A multiple of the stake from hundredths of a percent: `1.42×`. */
export const multiple = (bp: bigint | string | number) => `${fmt(fromRaw(BigInt(bp), 4), { places: 2 })}×`;

const LETTER = { win: "W", loss: "L", bust: "B" } as const;
const WORD = { win: "won", loss: "lost", bust: "busted" } as const;

/** The last calls, oldest on the left: a sports form guide. */
export function FormGuide({ plays, count = 5, size = "md" }: { plays: Play[]; count?: number; size?: "sm" | "md" }) {
  const recent = plays.slice(0, count).reverse();
  const blanks = Math.max(0, count - recent.length);
  const words = recent.map((play) => WORD[resultOf(play)]).join(", ");
  return (
    <span className={`form form-${size}`} role="img" aria-label={recent.length ? `Last ${recent.length}: ${words}` : "No calls yet"}>
      {Array.from({ length: blanks }, (_, index) => (
        <span key={`blank-${index}`} className="form-cell form-empty" />
      ))}
      {recent.map((play) => {
        const result = resultOf(play);
        return (
          <span key={play.id} className={`form-cell form-${result[0]}`}>
            {LETTER[result]}
          </span>
        );
      })}
    </span>
  );
}

function hue(address: string, from: number): number {
  const hex = address.replace(/^0x/, "").slice(from, from + 6) || "0";
  return Number.parseInt(hex, 16) % 360;
}

/** A wallet's colours, made from its address. */
export function Avatar({ address, size = 24 }: { address: string; size?: number }) {
  const a = hue(address, 2);
  const b = hue(address, 14);
  const style: CSSProperties = {
    width: size,
    height: size,
    background: `linear-gradient(135deg, hsl(${a} 42% 52%), hsl(${b} 48% 26%))`,
  };
  return <span className="account-avatar" style={style} aria-hidden />;
}

/** A figure that flashes the colour of its last move. */
export function useTickFlash<T extends HTMLElement>(value: bigint | null) {
  const ref = useRef<T>(null);
  const last = useRef<bigint | null>(null);
  useEffect(() => {
    const el = ref.current;
    const previous = last.current;
    last.current = value;
    if (!el || value === null || previous === null || previous === value) return;
    el.dataset.tick = value > previous ? "up" : "down";
    const timer = setTimeout(() => delete el.dataset.tick, 420);
    return () => clearTimeout(timer);
  }, [value]);
  return ref;
}
